import { useEffect, useState } from 'react';

import { fetchUserDetails } from '../BackendRoute';
import { useGuest } from '../GuestProvider';
import { useLanguage } from '../LanguageContext';
import { useSnackbar } from '../SnackbarContext';
import LoadingPage from '../components/LoadingPage';
import WalkingCat from '../components/WalkingCat';
import { getTokenFromCookie } from '../utils/cookieUtils';
import { preloadResources } from '../utils/preload';
import DesktopStory from './DesktopStory';
import MobileStory from './MobileStory';

const MOBILE_VIEW_WIDTH = 768;
const MOBILE_VIEW_MIN_HEIGHT = 600;
const MOBILE_ASPECT_RATIO_THRESHOLD = 1; // width/height < 1 means portrait

function isMobileView() {
  const { innerWidth: w, innerHeight: h } = window;
  return (
    w <= MOBILE_VIEW_WIDTH ||
    h <= MOBILE_VIEW_MIN_HEIGHT ||
    w / h < MOBILE_ASPECT_RATIO_THRESHOLD
  );
}

export default function MainPage() {
  const [mobile, setMobile] = useState(isMobileView());
  const [isLoading, setIsLoading] = useState(true);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const { guestDetails, setGuestDetails } = useGuest();
  const { showSnackbar } = useSnackbar();
  const {
    t,
    isLoading: isLanguageLoading,
    setLocaleFromBackend,
  } = useLanguage();

  useEffect(() => {
    const resize = () => setMobile(isMobileView());
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const queryUserDetails = async () => {
    try {
      // Try to use token from cookie, fallback to id from URL
      const token = getTokenFromCookie();
      if (!token) {
        showSnackbar(t('main.error_no_token'), 'error');
        setIsLoading(false);
        return;
      }
      const identifier = token;

      if (identifier) {
        const resp = await fetchUserDetails(identifier, showSnackbar);
        setGuestDetails(resp.main_guest);
        setLocaleFromBackend(resp.main_guest?.default_language);
      }
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    queryUserDetails();
    preloadResources((pct) => {
      setProgress(pct);
    }).then(() => {
      setResourcesLoaded(true);
    });
  }, []);

  if (isLoading || isLanguageLoading || !resourcesLoaded || !guestDetails) {
    return <LoadingPage progress={progress} />;
  }

  return (
    <div>
      {mobile ? (
        <MobileStory guestDetails={guestDetails} />
      ) : (
        <DesktopStory guestDetails={guestDetails} />
      )}
      <WalkingCat />
    </div>
  );
}
